import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { environment } from '@environments/environment';
import { SignInStore } from '@core/stores/sign-in.store';
import { parseAuthorizeRequest } from '@core/auth/authorize-request';

/**
 * Validates the /authorize query string BEFORE the sign-in UI activates.
 *
 * GPGT-41350: "Validate app_code and redirect_uri BEFORE rendering any UI. A foreign redirect_uri
 * renders an error page on accounts.gozem.co and never redirects." A guard is the only place in
 * Angular where "before any UI" is literally true — a check inside the component has already
 * rendered it.
 *
 * On rejection this navigates to our OWN error route and passes the REASON, not the redirect_uri.
 * The rejected URI is never echoed into a template, a query string or a link.
 *
 * LIVES IN core/guards, NOT core/auth. `core/auth` imports no Angular so that the rules there can be
 * typechecked and run without the framework (see docs/ARCHITECTURE.md, D-2); a guard is Angular by
 * definition. The decision it delegates to -- `parseAuthorizeRequest` -- stays on the pure side.
 */
export const authorizeGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const store = inject(SignInStore);

  const req = String(route.queryParams['req'] ?? '');

  /**
   * THE NORMAL PATH: a `req` handle and nothing else.
   *
   * The service's `GET /api/v1/sso/authorize` validated app_code, redirect_uri, state and the PKCE
   * challenge, minted a handle and answered `302 -> /login?req=req_...`. Those parameters stayed
   * with the service — that is what the handle is for — so THERE IS NOTHING HERE TO VALIDATE, and
   * demanding them would reject every real sign-in.
   *
   * The redirect target is not reconstructed later either: `/select-account` returns the
   * `redirect_uri` and `state` it validated, and the hand-off uses those.
   */
  if (req) {
    store.beginWithHandle(req);
    return true;
  }

  /**
   * THE DIRECT PATH: an application linked at this origin with the OAuth parameters instead of at
   * the service, so there is no handle yet.
   *
   * Here the parameters ARE present and ARE validated before any UI renders — GPGT-41350's rule —
   * and the page then bounces to the service to obtain a handle. A foreign redirect_uri is refused
   * on this origin and never forwarded.
   */
  const params = new URLSearchParams(
    Object.entries(route.queryParams).map(([key, value]) => [key, String(value)])
  );
  const result = parseAuthorizeRequest(params, environment.allowedRedirectOrigins);

  if (!result.ok) {
    return router.createUrlTree(['/error'], { queryParams: { reason: result.reason } });
  }

  store.beginDirect(result.request);
  return true;
};
